import {
  Component,
  DestroyRef,
  inject,
  signal
} from '@angular/core';

import {
  CommonModule
} from '@angular/common';

import {
  FormControl,
  FormGroup,
  ReactiveFormsModule
} from '@angular/forms';

import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';

import {
  MatIconModule
} from '@angular/material/icon';

import {
  debounceTime,
  distinctUntilChanged
} from 'rxjs';

import {
  takeUntilDestroyed
} from '@angular/core/rxjs-interop';

import { AuthService } from '../../../core/services/auth.service';
import { Button } from '../../components/button/button';
import { InputControl } from '../../components/input-control/input-control';
import { TextareaControl } from '../../components/textarea-control/textarea-control';
import { ISPUtil } from '../../utils/isp.util';
import { UserInfo } from '../../../core/models/auth.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

// ======================================================
// TYPES
// ======================================================

interface SubscriptionDialogData {
  isSubscriptionExpired: boolean;
  showForm: boolean;
  showDialogClose: boolean;
}

// ======================================================
// COMPONENT
// ======================================================

@Component({
  selector: 'isc-subscription-expiry-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatIconModule,
    Button,
    InputControl,
    TextareaControl
  ],
  templateUrl: './subscription-expiry-dialog.html',
  styleUrl: './subscription-expiry-dialog.scss'
})

export class SubscriptionExpiryDialog {

  // ======================================================
  // INJECT
  // ======================================================

  private readonly dialogRef =
    inject(MatDialogRef<SubscriptionExpiryDialog>);

  readonly data =
    inject<SubscriptionDialogData>(MAT_DIALOG_DATA);

  private readonly authService =
    inject(AuthService);

  private readonly destroyRef =
    inject(DestroyRef);

  // ======================================================
  // STATE
  // ======================================================

  readonly dataSent =
    signal(false);

  readonly showForm =
    signal(this.data.showForm);

  readonly isMessageValid =
    signal(false);

  // ======================================================
  // FORM
  // ======================================================

  readonly contactUsForm =
    new FormGroup({

      name:
        new FormControl({
          value: '',
          disabled: true
        }),

      registeredEmail:
        new FormControl({
          value: '',
          disabled: true
        }),

      subject:
        new FormControl({
          value: 'Subscription Renewal',
          disabled: true
        }),

      message:
        new FormControl('')
    });

  // ======================================================
  // CONSTRUCTOR
  // ======================================================

  constructor() {

    const userData = this.authService.getUserInformation<UserInfo|null>();
    let name = "";
    if(userData?.name){
      name = userData?.name;
    } else if(userData?.first_name || userData?.last_name) {
      name = userData?.first_name + "" + userData?.last_name
    }
    this.contactUsForm.patchValue({
      name: name,
      registeredEmail: userData?.email ?? ''
    });

    this.contactUsForm
      .get('message')
      ?.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((msg) => {

        if (!msg) {
          this.isMessageValid.set(false);
          return;
        }

        this.isMessageValid.set(
          this.validateMessage(msg)
        );
      });
  }

  // ======================================================
  // CLOSE
  // ======================================================

  close(): void {

    this.dialogRef.close();
  }

  // ======================================================
  // OPEN FORM
  // ======================================================

  openForm(): void {

    this.showForm.set(true);

    this.dataSent.set(false);
  }

  // ======================================================
  // SUBMIT
  // ======================================================

  submitForm(): void {

    const formData =
      this.contactUsForm.getRawValue();

    const payload = {
      subject: formData.subject ?? '',
      message: formData.message ?? ''
    };

    this.authService
      .contactToSales(payload)
      .subscribe({

        next: () => {

          this.dataSent.set(true);
        },

        error: (err) => {

          console.error(err);
        }
      });
  }

  // ======================================================
  // VALIDATE
  // ======================================================

  validateMessage(
    msg: string
  ): boolean {

    return ISPUtil.isAllowedString(msg);
  }
}